import React from "react";

/**
 * CareLogix™ ORACLE — Compliance Lock™ (Phase 26C)
 *
 * Single source of truth for the Phase 26 mandated compliance disclaimer.
 * Renders three additive variants without altering existing UI:
 *
 *   • <ComplianceLock variant="banner" />       — persistent page header strip
 *   • <ComplianceLock variant="footer" />       — persistent page footer strip
 *   • <ComplianceLock variant="inline" />       — small inline notice on cards
 *
 * Additive only. Never use this to remove other disclaimers — keep existing
 * ones intact.
 */

export const COMPLIANCE_LOCK_TEXT =
  "This is AI research for educational decision-support only, not financial " +
  "advice. Markets involve risk of loss. Past performance and backtests do not " +
  "predict future results. Human review is required before any trade, " +
  "investment, or wager.";

// Phase 27 — Mandated legal lock copy. Use via <ComplianceLock policy="v27" />
// while preserving the Phase 26 legacy copy above for backward compatibility.
export const COMPLIANCE_LOCK_TEXT_V27 =
  "Educational market intelligence only. Not financial advice. Not betting " +
  "advice. No guaranteed outcomes. Human confirmation required.";

export const COMPLIANCE_LOCK_SHORT =
  "AI research only · not financial advice · human review required";

export const COMPLIANCE_LOCK_SHORT_V27 =
  "Educational only · no guarantees · human confirmation required";

const STYLES = {
  banner: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "9px 18px",
    background:
      "linear-gradient(90deg, rgba(197,160,89,0.10), rgba(16,185,129,0.06))",
    border: "1px solid rgba(197,160,89,0.22)",
    color: "#fde68a",
    fontFamily: "'Space Mono', monospace",
    fontSize: 11.5,
    letterSpacing: "0.03em",
    lineHeight: 1.4,
  },
  footer: {
    display: "flex",
    alignItems: "flex-start",
    gap: 10,
    padding: "10px 18px",
    background: "rgba(11,15,20,0.85)",
    borderTop: "1px solid rgba(197,160,89,0.18)",
    color: "#cbd5e1",
    fontFamily: "'Space Mono', monospace",
    fontSize: 11,
    letterSpacing: "0.02em",
    lineHeight: 1.4,
  },
  inline: {
    display: "block",
    marginTop: 6,
    padding: "4px 8px",
    background: "rgba(197,160,89,0.08)",
    borderLeft: "2px solid rgba(197,160,89,0.55)",
    color: "#fde68a",
    fontFamily: "'Space Mono', monospace",
    fontSize: 10.5,
    letterSpacing: "0.02em",
    lineHeight: 1.45,
    borderRadius: 4,
  },
  card: {
    margin: "14px 0",
    padding: "12px 14px",
    background:
      "linear-gradient(135deg, rgba(197,160,89,0.06), rgba(16,185,129,0.03))",
    border: "1px solid rgba(197,160,89,0.24)",
    borderRadius: 10,
    color: "#fde68a",
    fontFamily: "'Space Mono', monospace",
    fontSize: 11,
    letterSpacing: "0.03em",
    lineHeight: 1.5,
  },
  badge: {
    color: "#c5a059",
    fontWeight: 800,
    letterSpacing: "0.10em",
    marginRight: 8,
    fontSize: 10.5,
  },
};

/**
 * @param {{
 *   variant?: "banner"|"footer"|"inline"|"card",
 *   short?: boolean,
 *   policy?: "v26"|"v27",
 *   className?: string,
 *   style?: React.CSSProperties,
 *   testid?: string,
 * }} props
 */
export default function ComplianceLock({
  variant = "banner",
  short = false,
  policy = "v27",
  className,
  style,
  testid,
}) {
  const base = STYLES[variant] || STYLES.banner;
  const useV27 = policy !== "v26";
  const text = short
    ? (useV27 ? COMPLIANCE_LOCK_SHORT_V27 : COMPLIANCE_LOCK_SHORT)
    : (useV27 ? COMPLIANCE_LOCK_TEXT_V27 : COMPLIANCE_LOCK_TEXT);
  const id = testid || `compliance-lock-${variant}`;

  return (
    <div
      role="note"
      aria-label="Compliance disclaimer"
      data-testid={id}
      className={className}
      style={{ ...base, ...(style || {}) }}
    >
      <span style={STYLES.badge} aria-hidden="true">
        ⚖ COMPLIANCE
      </span>
      <span>{text}</span>
    </div>
  );
}

export { ComplianceLock };
